import React, { useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";

interface VideoTimelineProps {
  duration: number;
  currentTime: number;
  trimStart: number;
  trimEnd: number;
  setTrimStart: (time: number) => void;
  setTrimEnd: (time: number) => void;
  setCurrentTime: (time: number) => void;
}

const VideoTimeline: React.FC<VideoTimelineProps> = ({
  duration,
  currentTime,
  trimStart,
  trimEnd,
  setTrimStart,
  setTrimEnd,
  setCurrentTime,
}) => {
  const theme = useTheme();
  const timelineRef = useRef<HTMLDivElement>(null);
  const [isDraggingTrimMarker, setIsDraggingTrimMarker] = useState(false);
  const [activeTrimMarker, setActiveTrimMarker] = useState<
    "start" | "end" | null
  >(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);

  // Calculate positions as percentages
  const startPosition = (trimStart / duration) * 100;
  const endPosition = (trimEnd / duration) * 100;
  const currentPosition = (currentTime / duration) * 100;

  const handleMouseDown =
    (marker: "start" | "end") => (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDraggingTrimMarker(true);
      setActiveTrimMarker(marker);
    };

  const handlePlayheadMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingPlayhead(true);
  };

  const handleTimelineClick = (e: React.MouseEvent) => {
    if (!timelineRef.current || isDraggingTrimMarker) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const time = Math.max(0, Math.min(duration, position * duration));
    setCurrentTime(time);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const time = Math.max(0, Math.min(duration, position * duration));

    if (isDraggingTrimMarker && activeTrimMarker) {
      if (activeTrimMarker === "start") {
        setTrimStart(Math.min(time, trimEnd - 0.5));
      } else {
        setTrimEnd(Math.max(time, trimStart + 0.5));
      }
    } else if (isDraggingPlayhead) {
      setCurrentTime(time);
    }
  };

  const handleMouseUp = () => {
    setIsDraggingTrimMarker(false);
    setActiveTrimMarker(null);
    setIsDraggingPlayhead(false);
  };

  // Add mouse events for drag operations
  React.useEffect(() => {
    if (isDraggingTrimMarker || isDraggingPlayhead) {
      document.addEventListener("mousemove", handleMouseMove as any);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove as any);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDraggingTrimMarker, isDraggingPlayhead, activeTrimMarker]);

  return (
    <Box
      ref={timelineRef}
      sx={{
        position: "relative",
        height: 40,
        cursor: "pointer",
        my: 1,
      }}
      onClick={handleTimelineClick}
      onMouseMove={handleMouseMove}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          height: 6,
          transform: "translateY(-50%)",
          bgcolor: theme.palette.grey[300],
          borderRadius: 3,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: `${startPosition}%`,
          width: `${endPosition - startPosition}%`,
          height: 6,
          transform: "translateY(-50%)",
          bgcolor: theme.palette.primary.main,
          borderRadius: 3,
          transition: isDraggingTrimMarker ? "none" : "all 0.1s ease",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: `${currentPosition}%`,
          width: 4,
          height: 24,
          bgcolor: theme.palette.error.main,
          borderRadius: 1,
          transform: "translate(-50%, -50%)",
          zIndex: 2,
          cursor: "ew-resize",
          transition: isDraggingPlayhead ? "none" : "left 0.1s linear",
          "&:hover": {
            width: 6,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: -12,
            left: "50%",
            transform: "translateX(-50%)",
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: `8px solid ${theme.palette.error.main}`,
          },
        }}
        onMouseDown={handlePlayheadMouseDown}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: `${startPosition}%`,
          width: 18,
          height: 18,
          bgcolor: theme.palette.primary.dark,
          borderRadius: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 3,
          cursor: "ew-resize",
          border: `2px solid ${theme.palette.common.white}`,
          boxShadow: 2,
          transition:
            isDraggingTrimMarker && activeTrimMarker === "start"
              ? "none"
              : "left 0.1s ease",
          "&:hover": {
            boxShadow: 4,
          },
        }}
        onMouseDown={handleMouseDown("start")}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: `${endPosition}%`,
          width: 18,
          height: 18,
          bgcolor: theme.palette.primary.dark,
          borderRadius: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 3,
          cursor: "ew-resize",
          border: `2px solid ${theme.palette.common.white}`,
          boxShadow: 2,
          transition:
            isDraggingTrimMarker && activeTrimMarker === "end"
              ? "none"
              : "left 0.1s ease",
          "&:hover": {
            boxShadow: 4,
          },
        }}
        onMouseDown={handleMouseDown("end")}
      />
    </Box>
  );
};

export default VideoTimeline;
